@file:OptIn(ExperimentalJsExport::class)

package nl.astraeus.wiki.components

import kotlinx.browser.window
import kotlinx.html.InputType
import kotlinx.html.a
import kotlinx.html.br
import kotlinx.html.classes
import kotlinx.html.code
import kotlinx.html.div
import kotlinx.html.h1
import kotlinx.html.h2
import kotlinx.html.h3
import kotlinx.html.h4
import kotlinx.html.h5
import kotlinx.html.hr
import kotlinx.html.i
import kotlinx.html.img
import kotlinx.html.input
import kotlinx.html.li
import kotlinx.html.ol
import kotlinx.html.p
import kotlinx.html.pre
import kotlinx.html.span
import kotlinx.html.strong
import kotlinx.html.style
import kotlinx.html.table
import kotlinx.html.tbody
import kotlinx.html.td
import kotlinx.html.th
import kotlinx.html.thead
import kotlinx.html.tr
import kotlinx.html.ul
import nl.astraeus.komp.HtmlBuilder
import nl.astraeus.komp.Komponent
import nl.astraeus.komp.currentElement
import nl.astraeus.markdown.parser.ListItem
import nl.astraeus.markdown.parser.MarkdownPart
import nl.astraeus.markdown.parser.parseParagraph
import nl.astraeus.wiki.service.ResourceService
import nl.astraeus.wiki.service.ResourceType
import org.w3c.dom.Node

@JsExport
data class MermaidOptions(
  @JsName("startOnLoad")
  val startOnLoad: Boolean = true,
  @JsName("logLeveL")
  val logLeveL: String = "info",
)

fun HtmlBuilder.renderPart(part: MarkdownPart) = when (part) {
  is MarkdownPart.Header -> {
    when (part.size) {
      1 -> h1 {
        +part.text
      }

      2 -> h2 {
        +part.text
      }

      3 -> h3 {
        +part.text
      }

      4 -> h4 {
        +part.text
      }

      5 ->
        h5 {
          +part.text
        }

      else -> {
        span {
          +part.text
        }
      }
    }
  }

  is MarkdownPart.Ruler -> hr {}

  is MarkdownPart.UnorderedList -> ul {
    renderUnorderedList(0, 0, part.lines)
  }

  is MarkdownPart.OrderedList -> ol {
    renderOrderedList(0, 0, part.lines)
  }

  is MarkdownPart.CheckboxList -> ul("no-bullets") {
    for (line in part.lines) {
      li {
        style = "margin-left: ${line.indentation / 2f}rem"
        /*
                if (!readOnly) {
                  onClick("toggle-checkbox" to "${entry.id}-${line.line}")
                }
        */

        input {
          type = InputType.checkBox
          checked = line.checked
          name = "checkbox-${line.line}"
          readonly = true
        }

        renderParagraph(parseParagraph(line.text))
      }
    }
  }

  is MarkdownPart.CodeBlock -> {
    if (part.language == "mermaid") {
      pre("mermaid") {
        +part.text
        ResourceService.loadResource(ResourceType.JS, "mermaid.min.js") {
          try {
            val errorHandler = { err: dynamic, hash: dynamic ->
              console.log("Error: ", err, hash)
            }
            window.asDynamic().mermaid.parseError = errorHandler
            window.asDynamic().mermaid.run()
          } catch (e: Throwable) {
            console.error(e)
          }
        }
      }
    } else if (part.language == "viz") {
      div {
        val element = currentElement()

        ResourceService.loadResource(ResourceType.JS, "viz.js") {
          window.asDynamic().Viz.instance().then { viz ->
            element.appendChild(viz.renderSVGElement(part.text) as Node)
          }
        }
      }
    } else if (part.language == "jax") {
      div {
        val element = currentElement()

        ResourceService.loadResource(
          ResourceType.JS,
          "https://cdn.jsdelivr.net/npm/mathjax@3/es5/tex-svg.js"
        ) { //""tex-mml-chtml.js") {
          val mathElement: Node = window.asDynamic().MathJax.tex2svg(part.text) as Node
          //console.log("Jax", mathElement)
          element.appendChild(mathElement)
        }
      }
    } else if (part.language == "wire" || part.language == "wireframe") {
      div {
        renderWireframe(part.text)
      }
    } else {
      pre {
        code(classes = "language-${part.language}") {
          +part.text

          val element = currentElement()
          ResourceService.loadResource(ResourceType.CSS, "styles/arduino-light.min.css")
          //ResourceService.loadResource(ResourceType.CSS, "styles/androidstudio.min.css")
          ResourceService.loadResource(ResourceType.JS, "highlight.js") {
            ResourceService.loadResource(ResourceType.JS, "languages/${part.language}.min.js") {
              window.asDynamic().hljs.highlightElement(element)
            }
          }
        }
      }
    }
  }

  is MarkdownPart.NewLine -> span { }

  is MarkdownPart.Paragraph -> p {
    renderParagraph(part)
  }

  is MarkdownPart.Table -> table("table table-bordered") {
    thead {
      tr {
        for (h in part.headers) {
          th { +h }
        }
      }
    }
    tbody {
      for ((index, row) in part.rows.withIndex()) {
        tr {
          if (index % 2 == 0) {
            classes += "even"
          } else {
            classes += "odd"
          }
          for (cell in row) {
            td { +cell }
          }
        }
      }
    }
  }

  is MarkdownPart.PageBreak -> hr("page-break") {}
}

fun HtmlBuilder.renderParagraph(part: MarkdownPart.Paragraph) {
  for (part in part.parts) {
    when (part) {
      is MarkdownPart.ParagraphPart.Bold -> strong {
        +part.text
      }

      is MarkdownPart.ParagraphPart.BoldItalic -> strong {
        i {
          +part.text
        }
      }

      is MarkdownPart.ParagraphPart.Image -> if (part.url != null) {
        a {
          href = part.url ?: ""
          img {
            src = part.src
            alt = part.alt
          }
        }
      } else {
        img {
          src = part.src
          alt = part.alt
        }
      }
      is MarkdownPart.ParagraphPart.Italic -> i {
        +part.text
      }

      is MarkdownPart.ParagraphPart.Link -> a {
        href = part.url
        +(part.label ?: part.url)
      }

      is MarkdownPart.ParagraphPart.Text -> span {
        +part.text
      }

      is MarkdownPart.ParagraphPart.InlineCode -> span("inline-code") {
        +part.text
      }

      is MarkdownPart.ParagraphPart.StrikeThrough -> span("strike-through") {
        +part.text
      }

      is MarkdownPart.ParagraphPart.LineBreak -> br {}

      is MarkdownPart.ParagraphPart.IndentedCode -> span("indented-code") {
        +part.code
      }
    }
  }
}


private fun HtmlBuilder.renderUnorderedList(
  startIndex: Int,
  indent: Int,
  list: List<ListItem>
): Int {
  var index = startIndex

  while (index < list.size) {
    val line = list[index]
    if (line.indentation > indent) {
      ul {
        index += renderUnorderedList(index, line.indentation, list)
      }
    } else if (line.indentation < indent) {
      return index - startIndex
    } else {
      li {
        renderParagraph(parseParagraph(line.text))
      }
      index++
    }
  }

  return index - startIndex
}

private fun HtmlBuilder.renderOrderedList(
  startIndex: Int,
  indent: Int,
  list: List<ListItem>
): Int {
  var index = startIndex

  while (index < list.size) {
    val line = list[index]
    if (line.indentation > indent) {
      ol {
        index += renderOrderedList(index, line.indentation, list)
      }
    } else if (line.indentation < indent) {
      return index - startIndex
    } else {
      li {
        renderParagraph(parseParagraph(line.text))
      }
      index++
    }
  }

  return index - startIndex
}

class MarkdownRenderer(
  val parts: List<MarkdownPart>
) : Komponent() {

  override fun HtmlBuilder.render() {
    div {
      for (part in parts) {
        renderPart(part)
      }
    }
  }
}
